import type { IDedicatedResourceRef } from "@gettactic/api";

/** A calendar day as the API sends it and the agenda keys its days: `yyyy-MM-dd`. */
const DAY = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Whether the desk was already the person's on the day (`yyyy-MM-dd`): the
 * assignment began on or before it. A desk without a known start counts from
 * today only, never for the past.
 *
 * Both are compared as the labels they are, with no zone read into either.
 * They used to be read through one, which is what a timestamp would need, and
 * that is the mistake this shape removes: midnight on the day the desk became
 * theirs, converted into any office west of Greenwich, is still the day before.
 *
 * The labels do not come from the same calendar. The agenda's rows are the
 * viewer's days (`useMyAgenda` is asked in `deviceTz`) and `dedicated_since` is
 * the office's. That matters on one row only - the day the desk became theirs,
 * which a viewer in another zone can see a day out - and no arithmetic here can
 * mend it, because a row spans two of the office's days and this side is not
 * told which part of it to take. Releases do not have the problem: one written
 * from a row carries that row's own label, so it matches the row it came from.
 */
export const isDeskDedicatedOn = (
  desk: Pick<IDedicatedResourceRef, "dedicated_since">,
  date: string,
  today: string
): boolean => {
  const since = desk.dedicated_since;
  if (!since || !DAY.test(since)) {
    return date >= today;
  }
  return date >= since;
};
